import { FunctionComponent as FC, useEffect, useState } from 'react';
import { withStyles, Card, DropdownMenu, Icon, MaterialSymbol } from 'vopay';

import type { ClientAccountBalance, SupportedCurrencies } from 'vopay';
import styles from './PaymentMethodsTab.module.scss';
import useFetchPaylinkSettings from '../hooks/useFetchElinxSettings';
import type { FormValues as AddInteracFormValues } from './AddInteracModal';
import PaymentMethodDetails from './PaymentMethodDetails';
import { useToast } from '#shared/providers/Toast';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useDialog } from '#shared/providers/Dialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import AddMethodMenu from './AddMethodMenu';
import ProvidePaymentMethod from './ProvidePaymentMethod/ProvidePaymentMethod';
import ElinxRequestDialog from './ElinxRequestDialog';
import PaymentMethodListItem from './PaymentMethodListItem/PaymentMethodListItem';
import { useTranslation } from 'react-i18next';
import { useHostNavigate } from '../HostNavigationContext';
import usePaymentMethods, { PaymentMethod } from '../hooks/usePaymentMethods';

import { Button } from '#shared/components/UI';

const cn = withStyles(styles);

type UsePaymentMethods = ReturnType<typeof usePaymentMethods>;

export type PaymentMethodsTabProps = {
  groupedMethods: UsePaymentMethods['groupedMethods'];
  // The hosts' refresh wrappers surface fetch failures themselves and then
  // resolve undefined, so the return is looser than fetchMethods'.
  onRefresh: () => Promise<PaymentMethod[] | undefined>;
  onSetDefaultMethod: UsePaymentMethods['setDefaultMethod'];
  onDeleteMethod: UsePaymentMethods['deleteMethod'];
  onDeleteGroupedMethod: UsePaymentMethods['deleteGroupedMethod'];
  accountID: string;
  accountName: string;
  email: string;
  balances?: ClientAccountBalance[];
  active: boolean;
  accountPhone?: string;
  accountAddress1?: string;
  accountCity?: string;
  accountProvince?: string;
  accountPostalCode?: string;
  accountCountry?: string;
  // Host facts the portal read from its ClientData provider.
  clientAccountsEnabled: boolean;
  country: string;
  loginUserName?: string;
  // The signed-in account's own name (portal: ClientData AccountName) — the
  // default sender name on the eLinx request form. Absent: the field starts empty.
  senderName?: string;
  // Feature flags the portal read from its LockedFeatures provider.
  // Absent means the feature behaves as locked/off.
  isPayLinkEnabled?: boolean;
  isMicroDepositBankAccountVerificationEnabled?: boolean;
  isIQ11DataEnrichingEnabled?: boolean;
  isIQ11DataEnrichingEnhancedEnabled?: boolean;
  isInteracBasicVerificationEnabled?: boolean;
  isInteracEnhancedVerificationEnabled?: boolean;
  isCardBasicVerificationEnabled?: boolean;
  isCardEnhancedVerificationEnabled?: boolean;
  // Per-feature lock map (portal: LockedFeatures.LockedSelectOptions). Absent: nothing locked.
  lockedSelectOptions?: { [featureKey: string]: string };
  // User permissions (portal: Permissions provider). Absent means not permitted.
  hasViewBankAccountTransactionHistoryPermission?: boolean;
  hasViewElinxSettingsPermission?: boolean;
  // The host's locked-feature upsell dialog (portal: LockedFeatureDialogue
  // provider). Absent: clicking a locked feature does nothing.
  onLockedFeature?: (featureName: string) => void;
};

const PaymentMethodsTab: FC<PaymentMethodsTabProps> = ({
  groupedMethods,
  onRefresh,
  onSetDefaultMethod,
  onDeleteMethod,
  onDeleteGroupedMethod,
  accountID,
  accountName,
  email,
  balances,
  active,
  accountPhone,
  accountAddress1,
  accountCity,
  accountProvince,
  accountPostalCode,
  accountCountry,
  clientAccountsEnabled,
  country,
  loginUserName = '',
  senderName,
  isPayLinkEnabled,
  isMicroDepositBankAccountVerificationEnabled,
  isIQ11DataEnrichingEnabled,
  isIQ11DataEnrichingEnhancedEnabled,
  isInteracBasicVerificationEnabled,
  isInteracEnhancedVerificationEnabled,
  isCardBasicVerificationEnabled,
  isCardEnhancedVerificationEnabled,
  lockedSelectOptions,
  hasViewBankAccountTransactionHistoryPermission,
  hasViewElinxSettingsPermission,
  onLockedFeature,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.Customers.PaymentMethodsTab' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Shared' });
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  // Captured here and passed into dialog.open content — dialogs render under
  // the DialogProvider, above the HostNavigationProvider.
  const hostNavigate = useHostNavigate();

  const [showingAddMethod, setShowingAddMethod] = useState(false);
  const { settings: paylinkSetting, fetchElinxSettings: fetchPaylinkSetting } = useFetchPaylinkSettings();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [addPaymentDropdownExpanded, setAddPaymentDropdownExpanded] = useState(false);
  const dialog = useDialog();

  useEffect(() => {
    fetchPaylinkSetting({
      PortalUser: loginUserName,
    })
      // On failure the hook already logged; the details pane simply stays hidden.
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelectedMethod(groupedMethods.find((m) => m.token === selectedMethod?.token) || groupedMethods[0]);
  }, [groupedMethods]);

  async function handleSetDefault(method: PaymentMethod) {
    const { type, token, details, typeLabel } = method;

    try {
      blockingLoader.show();

      const res = await onSetDefaultMethod(type, token);

      if (res.Success) {
        await onRefresh();
        toast.success(`${details} set as default ${type} for ${typeLabel}`);
      }
    } finally {
      blockingLoader.hide();
    }
  }

  const handleMethodAdded = async (type?: string) => {
    await onRefresh();
    setShowingAddMethod(false);

    if (type) {
      toast.success(
        t('Toast.AddMethod.added', {
          defaultValue: type + ' added',
          type,
        }),
      );
    }
  };

  const handleBankAdded = () => {
    handleMethodAdded(t('Toast.AddMethod.bankAccount', { defaultValue: 'Bank account' }));
  };

  const handleCreditCardAdded = async (token: string) => {
    const alreadyHasCreditCard = groupedMethods.some((m) => m.type === 'CreditCard');

    if (!alreadyHasCreditCard) {
      const res = await onSetDefaultMethod('CreditCard', token);

      if (res.ErrorMessage) {
        await alertDialog.open(
          t('Dialog.AddMethod.assignDefault', {
            defaultValue: 'Payment method has been successfully added. Please assign a default payment method',
          }),
          null,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
          },
        );
      }
    }

    handleMethodAdded(t('Toast.AddMethod.creditCard', { defaultValue: 'Credit card' }));
  };

  const handleDebitCardAdded = async (token: string) => {
    const alreadyHasDebitCard = groupedMethods.some((m) => m.type === 'DebitCard');

    if (!alreadyHasDebitCard) {
      const res = await onSetDefaultMethod('DebitCard', token);

      if (res.ErrorMessage) {
        await alertDialog.open(
          t('Dialog.AddMethod.assignDefault', {
            defaultValue: 'Payment method has been successfully added. Please assign a default payment method',
          }),
          null,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
          },
        );
      }
    }

    handleMethodAdded(t('Toast.AddMethod.debitCard', { defaultValue: 'Debit card' }));
  };

  const handleAddInterac = async (token: string, eTransferMethod: AddInteracFormValues['eTransferMethod']) => {
    const hasAtLeastOneInterac = groupedMethods.some((m) => ['Email', 'PhoneNumber'].includes(m.type));

    if (!hasAtLeastOneInterac) {
      const setDefaultResponse = await onSetDefaultMethod('Email', token);

      if (!setDefaultResponse.Success) {
        console.error(setDefaultResponse.ErrorMessage);

        await alertDialog.open(
          t('Dialog.AddMethod.assignDefault', {
            defaultValue: 'Payment method has been successfully added. Please assign a default payment method',
          }),
          null,
          {
            abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
          },
        );

        handleMethodAdded();

        return;
      }
    }

    if (eTransferMethod === 'Email') {
      handleMethodAdded(
        t('Toast.AddMethod.email', {
          defaultValue: 'Email',
        }),
      );
    } else {
      handleMethodAdded(
        t('Toast.AddMethod.phone', {
          defaultValue: 'Phone number',
        }),
      );
    }
  };

  const handleDeletePaymentMethod = async (method: PaymentMethod) => {
    const contextTypeLabel =
      method.kind === 'PHONE'
        ? 'Phone Number'
        : method.kind === 'EMAIL'
          ? 'Email Address'
          : ['GooglePay', 'ApplePay'].includes(method.type)
            ? 'Credit Card'
            : method.typeLabel;

    const decision = await alertDialog.caution(
      t('Dialog.Delete.title', {
        defaultValue: 'Delete {{typeLabel}}',
        typeLabel: contextTypeLabel,
        context: method.type,
      }),
      t('Dialog.Delete.content', {
        defaultValue: 'This action will delete {{typeLabel}} permanently.',
        typeLabel: contextTypeLabel,
        context: method.type,
      }),
      {
        abortLabel: tGlobal('Buttons.cancel', { defaultValue: 'Cancel' }),
        confirmLabel: t('Dialog.Delete.confirmLabel', { defaultValue: 'Delete' }),
      },
    );

    if (decision !== 'confirm') return;

    const res = await onDeleteGroupedMethod(method);
    const hasError = res.some((r) => !r.Success);

    if (hasError) {
      alertDialog.error(
        t('Toast.Delete.error', {
          defaultValue: 'Something went wrong trying to delete your account.',
        }),
        null,
        {
          abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
        },
      );
    } else {
      toast.neutral(
        t('Toast.Delete.success', {
          defaultValue: `${method.details} ${method.type} has been deleted.`,
          details: method.details,
          type: method.type,
        }),
      );
    }

    onRefresh();
  };

  const handleDeletePaymentRail = async (method: PaymentMethod) => {
    const decision = await alertDialog.caution(
      t('Dialog.DeleteRail.title', {
        defaultValue: `Delete ${method.typeLabel}`,
        typeLabel: method.typeLabel,
      }),
      t('Dialog.DeleteRail.content', {
        defaultValue: `This action will delete the ${method.typeLabel} information associated with ${method.details}.`,
        typeLabel: method.typeLabel,
        details: method.details,
      }),
      {
        abortLabel: tGlobal('Buttons.cancel', { defaultValue: 'Cancel' }),
        confirmLabel: t('Dialog.Delete.confirmLabel', { defaultValue: 'Delete' }),
      },
    );

    if (decision !== 'confirm') return;

    const res = await onDeleteMethod(method);

    if (res.Success) {
      await onRefresh();

      toast.neutral(
        t('Toast.Delete.success', {
          defaultValue: `${method.details} ${method.type} has been deleted.`,
          details: method.details,
          type: method.type,
        }),
      );
    } else {
      console.error(res.ErrorMessage);

      alertDialog.error(
        t('Toast.Delete.error', {
          defaultValue: 'Something went wrong trying to delete your account.',
        }),
        null,
        {
          abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
        },
      );
    }
  };

  async function handleRequest() {
    await dialog.open(
      ({ onCancel }) => (
        <ElinxRequestDialog
          accountName={accountName}
          accountID={accountID}
          email={email}
          onClose={onCancel}
          clientAccountsEnabled={clientAccountsEnabled}
          country={country}
          senderName={senderName}
          lockedSelectOptions={lockedSelectOptions}
          onLockedFeature={onLockedFeature}
          hostNavigate={hostNavigate}
        />
      ),
      // The dialog card supplies its own surface.
      { bare: true },
    );
  }

  return (
    <>
      {groupedMethods.length > 0 && (
        <div className={cn('paymentMethodsContainer')}>
          <section className={cn('left')}>
            <header className={cn('listHeader')}>
              {t('tabTitle', { defaultValue: 'Payment Methods' })}

              <AddMethodMenu
                disabled={!active}
                onRequestClick={handleRequest}
                onProvideClick={() => setShowingAddMethod(true)}
              />
            </header>

            <div className={cn('methodsContainer')}>
              <ul data-testid="paymentMethods-list" className={cn('methods', 'scrollbar')}>
                {groupedMethods.map((m, i) => (
                  // TODO: remove this m.token + i line when duplicated token data is fixed
                  <li data-testid={`paymentMethod-${i}`} key={m.token + i} onClick={() => setSelectedMethod(m)}>
                    <PaymentMethodListItem
                      method={m}
                      selected={selectedMethod?.token === m.token}
                      onSetDefault={handleSetDefault}
                      active={active}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {selectedMethod && (
            <section className={cn('right')} aria-label={`details for ${selectedMethod?.details}`}>
              <Card
                className={cn('detailsCard')}
                style={{
                  padding: '1px', // fix bottom border quirk when scrolling
                  shadowOpacity: 0.12,
                  dropShadow: '0px 10px 12px #2D2D2D1A',
                }}
              >
                <div className={cn('detailsContainer', 'scrollbar')}>
                  {paylinkSetting && selectedMethod && (
                    <PaymentMethodDetails
                      accountID={accountID}
                      method={selectedMethod}
                      email={email}
                      defaultCurrency={balances ? (balances[0].Currency as SupportedCurrencies) : 'CAD'}
                      onDeleteRail={handleDeletePaymentRail}
                      onDeleteMethod={handleDeletePaymentMethod}
                      onRefreshDetails={onRefresh}
                      onSetDefault={handleSetDefault}
                      active={active}
                      accountPhone={accountPhone}
                      accountAddress1={accountAddress1}
                      accountCity={accountCity}
                      accountProvince={accountProvince}
                      accountPostalCode={accountPostalCode}
                      accountCountry={accountCountry}
                      clientAccountsEnabled={clientAccountsEnabled}
                      loginUserName={loginUserName}
                      hasViewBankAccountTransactionHistoryPermission={hasViewBankAccountTransactionHistoryPermission}
                      hasViewElinxSettingsPermission={hasViewElinxSettingsPermission}
                      isPayLinkEnabled={isPayLinkEnabled}
                      isMicroDepositBankAccountVerificationEnabled={isMicroDepositBankAccountVerificationEnabled}
                      isIQ11DataEnrichingEnabled={isIQ11DataEnrichingEnabled}
                      isIQ11DataEnrichingEnhancedEnabled={isIQ11DataEnrichingEnhancedEnabled}
                      isInteracBasicVerificationEnabled={isInteracBasicVerificationEnabled}
                      isInteracEnhancedVerificationEnabled={isInteracEnhancedVerificationEnabled}
                      isCardBasicVerificationEnabled={isCardBasicVerificationEnabled}
                      isCardEnhancedVerificationEnabled={isCardEnhancedVerificationEnabled}
                      onLockedFeature={onLockedFeature}
                    />
                  )}
                </div>
              </Card>
            </section>
          )}
        </div>
      )}

      {groupedMethods.length === 0 && (
        <div className={cn('emptyState')}>
          <Icon icon="bank3" color="grey" height="67px" width="67px" />

          <span className={cn('emptyStateContent')}>
            <h4 className={cn('emptyStateTitle')}>
              {t('EmptyState.title', { defaultValue: 'No Payment Methods Added' })}
            </h4>

            <p className={cn('emptyStateDescription')}>
              {t('EmptyState.description', {
                defaultValue: "You haven't linked a payment method to this contact yet. Add one to get started.",
              })}
            </p>
          </span>

          <div>
            <DropdownMenu onOpenChange={setAddPaymentDropdownExpanded}>
              <DropdownMenu.Trigger asChild>
                <Button
                  iconRightProps={{
                    icon: addPaymentDropdownExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down',
                    fillAmount: 1,
                  }}
                  colorScheme="primary"
                  onClick={() => {
                    setAddPaymentDropdownExpanded(!addPaymentDropdownExpanded);
                  }}
                >
                  {t('EmptyState.addButton', { defaultValue: 'Add Payment Method' })}
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="end">
                <DropdownMenu.Item
                  key="request"
                  onSelect={handleRequest}
                  left={<MaterialSymbol icon="call_received" size="18px" />}
                >
                  {t('EmptyState.request', { defaultValue: 'Request' })}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  key="provide"
                  onSelect={() => setShowingAddMethod(true)}
                  left={<MaterialSymbol icon="article" fillAmount={1} size="18px" />}
                >
                  {t('EmptyState.provide', { defaultValue: 'Provide' })}
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </div>
        </div>
      )}

      <ProvidePaymentMethod
        isOpen={showingAddMethod}
        accountName={accountName}
        accountID={accountID}
        onClose={() => setShowingAddMethod(false)}
        onAddInterac={handleAddInterac}
        onBankAdded={handleBankAdded}
        onCreditCardAdded={handleCreditCardAdded}
        onDebitCardAdded={handleDebitCardAdded}
        onDigitalWalletAdded={(type) => handleMethodAdded(type)}
        clientAccountsEnabled={clientAccountsEnabled}
        country={country}
        loginUserName={loginUserName}
        lockedSelectOptions={lockedSelectOptions}
        onLockedFeature={onLockedFeature}
      />
    </>
  );
};

export default PaymentMethodsTab;
